import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { EASE, IMAGES, fmtCompact, fmtUSD, scrollToId } from "../lib/site";

const LINES = ["Direct aid.", "Dignified lives.", "A million hopes."];

export const Hero = ({ campaign }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);

  const raised = campaign?.raised ?? 0;
  const goal = campaign?.goal ?? 5000000;
  const donors = campaign?.donors ?? 0;
  const pct = campaign?.percent ?? 0;

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-28 sm:pt-36">
      <div
        className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full opacity-25 blur-3xl"
        style={{ background: "radial-gradient(circle, #D97706 0%, transparent 70%)" }}
      />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
              className="eyebrow mb-8"
            >
              The Million Hope Initiative · Newark, NJ
            </motion.p>
            <h1 className="font-serif text-5xl font-medium leading-[1.04] tracking-tight text-ink sm:text-6xl lg:text-7xl">
              {LINES.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${i === 2 ? "italic text-brand" : ""}`}
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1, delay: 0.2 + i * 0.13, ease: EASE }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
              className="mt-7 max-w-xl text-base leading-relaxed text-inksoft sm:text-lg"
            >
              Direct Assistance International Organizations is a New Jersey
              nonprofit delivering food, shelter, education and urgent care to
              poor families and underprivileged communities — across New Jersey
              and around the world.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="btn-hero-pledge-now"
                onClick={() => scrollToId("#donate")}
                className="group inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 text-sm font-semibold text-[#FDFBF7] transition-colors hover:bg-branddeep"
              >
                Donate now
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                data-testid="btn-hero-explore-programs"
                onClick={() => scrollToId("#programs")}
                className="rounded-full border border-ink/20 px-8 py-4 text-sm font-semibold text-ink transition-colors hover:border-brand hover:text-brand"
              >
                Explore programs
              </button>
            </motion.div>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div
              style={{ y }}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.4, ease: EASE }}
              className="relative mx-auto w-4/5 max-w-sm"
            >
              <div className="overflow-hidden rounded-b-[28px] rounded-t-[999px] border border-line shadow-2xl shadow-brand/10">
                <img
                  src={IMAGES.hero}
                  alt="Community members sharing a meal"
                  className="aspect-[3/4] w-full object-cover"
                />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
                className="absolute -left-10 bottom-10 rounded-2xl border border-line bg-cardx/95 px-5 py-4 shadow-xl backdrop-blur"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-inksoft">
                  Funded to date
                </p>
                <p className="mt-1 font-serif text-3xl font-semibold text-brand">
                  {pct}%
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.2 }}
        className="mt-16 border-y border-line bg-linen/60 sm:mt-20"
        data-testid="hero-campaign-ticker"
      >
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-10 gap-y-2 px-5 py-4 font-mono text-[11px] uppercase tracking-[0.18em] text-inksoft sm:px-8">
          <span className="flex items-center gap-2 text-brand">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-brand" />
            </span>
            Live campaign
          </span>
          <span data-testid="ticker-raised">Raised {fmtUSD(raised)}</span>
          <span>Goal {fmtUSD(goal)}</span>
          <span>{fmtCompact(donors)} donors</span>
          <span>Every gift traced to families</span>
        </div>
      </motion.div>
    </section>
  );
};
